// 08-15-21 xt: added agencyStubs.lastUpdated code per vickie to make the console wait for agency stubs to populate.
// 06/15/25 xt: replaced the react-bootstrap with Bootstrap 5.3.3 code to not be dependent on react-bootstrap
// src/views/Dashboard/Dashboard.js
// 20250719: need to fix so that on refresh it keeps the agency selected and when going bwtween tabs
import React, { useEffect, useRef, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
//import NewItemTone from "../../assets/sounds/app-tone.mp3";
//import WebChatChime from "../../assets/sounds/webchatchime.mp3";
import LocalizationService from "../../utils/LocalizationService";
import { getAgencyStatusMap } from "../../utils/ObjectFilters";
import { requestStubs, selectAgencyId } from "../../actions/agencyStubs";
import { setError } from "../../actions/findError"; 
import { fetchAgency } from "../../actions/agency";
import {
  fetchReportSummaries,
  dismissNotification,
  clearNewItem,
  setReportVisibility,
  reportSummariesTabStatus,
} from "../../actions/reportSummaries";
import AgencyList from "../../components/Agencies/AgencyList";
import ReportSummaries from "../../components/ReportSummaries/ReportSummaries";
import ModalRoot from "../../components/Modal/ModalRoot";
import Notification from "../Notification/Notification";
import StateTabs from "../StateTabs/StateTabs";
import { getVisibleReportSummaries } from "../../selectors/reportSummaries";

const POLL_INTERVAL = 10000;

export default function Dashboard() {
  const dispatch = useDispatch();
  const tokenRef = useRef(null);
  const params = useParams();
  
  const { getAccessTokenSilently } = useAuth0();
  const agencyStubs = useSelector((s) => s.agencyStubs);
  const agency = useSelector((s) => s.agency);
  const reportSummaries = useSelector(getVisibleReportSummaries);
  const error = useSelector((s) => s.error);
  const tabStatus = useSelector((s) => s.reportSummaries.tabStatus);
  const languageCode = LocalizationService.getCurrentLanguage();

  const [newAudioReady, setNewAudioReady] = useState(false);
  const [chimeAudioReady, setChimeAudioReady] = useState(false);
  const newAudioRef = useRef(null);
  const chimeAudioRef = useRef(null);
  const pollTimer = useRef(null);
  const [rowPerPage, setRowPerPage] = useState(10);
  const [curPage, setCurPage] = useState(0);
  const [newAndOpen, setNewAndOpen] = useState(0);
  const [newPage, setNewPage] = useState(0);
  const [open, setOpen] = useState(0);
  const [closed, setClosed] = useState(0);
  const [chatting, setChatting] = useState(0);

  const profile = useSelector((state) => state.profile);
  const redirectUri = process.env.REACT_APP_AUTH0_CALLBACK_URL;


  const prepareFetch = useCallback((agencyId, filter) => {
      try {
      const token = tokenRef.current;

      if (!token) {
      console.error("Dashboard.js - prepareFetch: No token available");
      return;
    }
      if (!agencyId) {
        console.log('Dashboard.js - prepareFetch: No agencyId, skipping fetch');
        return;
      }

      if (!agency.details?._id) {
        console.log(
          'Dashboard.js - prepareFetch: Agency details not ready, skipping fetch');
        return;
      }

      const visibilityFilter = filter || tabStatus || "SHOW_New & Open";
      const isChatting  = visibilityFilter === "SHOW_CHATTING";
      const isAll = visibilityFilter === "SHOW_ALL"; //but we never want to all this

      let filterIds = [];
      
      if (!isChatting && !isAll && visibilityFilter.includes("SHOW_")) {
      const statusKey = visibilityFilter.split("_")[1]; // e.g., "New", "Open"
      const statusMap = getAgencyStatusMap(agency.details, statusKey);
      if (statusMap) {
        filterIds = statusMap.report_statuses;
      }
    }
      dispatch(
        fetchReportSummaries(
          agencyId,
          filterIds,
          isChatting,
          rowPerPage,
          curPage,
          visibilityFilter,
          agency.details.name?.[languageCode] || "UNKNOWN",
          token
        )
      );
      } catch (err) {
      console.error("🔥 Dashboard.js - prepareFetch caught unexpected error", err);
      // You can also trigger an alert or fallback UI here
    }
    },
    [dispatch, agency.details, tabStatus, rowPerPage, curPage]
  );

  // Initial load and tab status
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = await getAccessTokenSilently();
        tokenRef.current = token; // this stores token??
        console.log('Dashboard.js - Access token fetched:', token);
 
        if (!agencyStubs.selectedAgencyId) {
           console.log('✅ Calling requestStubs() to fetch agency list...');
          dispatch(requestStubs(token)); // xt this must go and get the list of agencies sp user can select one.
        } else if (!agency.details._id) {
          await dispatch(fetchAgency(agencyStubs.selectedAgencyId, token));
          prepareFetch(agencyStubs.selectedAgencyId); // xt this looks like it will pull in all the agency info needed
        }
      } catch (err) {
        console.error('Dashboard.js - Failed to fetch token:', err);
        dispatch(setError(null, 'Token Error', 'Failed to authenticate. Please log in again.'));
      }
    };

    fetchData();
  }, [dispatch, getAccessTokenSilently, agencyStubs.selectedAgencyId, agency.details._id, prepareFetch]);

  // Auto-select single agency by URL or fallback
  useEffect(() => {
    const idFromUrl = params.id;
    if (!agencyStubs.selectedAgencyId && agencyStubs.items.length && idFromUrl) {
  dispatch(selectAgencyId(idFromUrl));
}

  }, [dispatch, agencyStubs.items, agencyStubs.selectedAgencyId, params.id]);


  // Polling and update handling
  useEffect(() => {
  if (tokenRef.current && agencyStubs.selectedAgencyId) {
    prepareFetch(agencyStubs.selectedAgencyId);
  } else {
    console.log('⏳ Skipping prepareFetch: Token or agency ID not ready');
  }
}, [prepareFetch, agencyStubs.selectedAgencyId, tabStatus]);

  useEffect(() => {
    if (pollTimer.current) clearTimeout(pollTimer.current);
    pollTimer.current = setTimeout(
      () => prepareFetch(agencyStubs.selectedAgencyId),
      POLL_INTERVAL
    );
    return () => clearTimeout(pollTimer.current);
  }, [reportSummaries.items, agencyStubs.selectedAgencyId, prepareFetch]);

  // First user gesture for audio readiness
  // useEffect(() => {
  //   const handler = () => {
  //     if (newAudioRef.current && chimeAudioRef.current) {
  //       Promise.all([
  //         newAudioRef.current.play().catch(() => {}),
  //         chimeAudioRef.current.play().catch(() => {}),
  //       ]).then(() => {
  //         setNewAudioReady(true);
  //         setChimeAudioReady(true);
  //       });
  //     }
  //     document.removeEventListener("click", handler);
  //   };
  //   document.addEventListener("click", handler);
  //   return () => document.removeEventListener("click", handler);
  // }, []);

  //const dismissNotification = () => dispatch(dismissNotification());
  const toggleTabs = (evt) => {
    dispatch(reportSummariesTabStatus(evt));
    dispatch(setReportVisibility(evt));
    let newCurPage;
    if (evt === "SHOW_New & Open") newCurPage = newAndOpen;
    else if (evt === "SHOW_New") newCurPage = newPage;
    else if (evt === "SHOW_Open") newCurPage = open;
    else if (evt === "SHOW_Closed") newCurPage = closed;
    else if (evt === "SHOW_CHATTING") newCurPage = chatting;
    else newCurPage = curPage;
    setCurPage(newCurPage);
    prepareFetch(agencyStubs.selectedAgencyId, evt);
  };

  const updateRowPerPage = (pageSize) => {
    setRowPerPage(pageSize);
    setCurPage(0);
    prepareFetch(agencyStubs.selectedAgencyId);
  };

  const updateCurPage = (page) => {
    if (tabStatus === "SHOW_New & Open") setNewAndOpen(page);
    else if (tabStatus === "SHOW_New") setNewPage(page);
    else if (tabStatus === "SHOW_Open") setOpen(page);
    else if (tabStatus === "SHOW_Closed") setClosed(page);
    else if (tabStatus === "SHOW_CHATTING") setChatting(page);
    setCurPage(page);

    prepareFetch(agencyStubs.selectedAgencyId);
  };

  // Moved early return after hooks
  console.log("DASHBOARD AGENCY CHECK >>>", {
  selectedAgencyId: agencyStubs.selectedAgencyId,
  agencyStubsItems: agencyStubs.items,
  agencyStubsCount: agencyStubs.items?.length,
  agency: agency,
});
  if (
  (!agencyStubs.selectedAgencyId || !agency.details._id) &&
  agencyStubs.items &&
  agencyStubs.items.length > 0
) {
  return (
    <AgencyList
      pathRef="/dashboard"
      agencies={agencyStubs.items}
      languageCode={languageCode}
    />
  );
}

  const userTimestamps = reportSummaries.items
    .map((r) => r.chat_summary?.user_last_received)
    .filter(Boolean)
    .sort((a, b) => b - a);
  const latest = userTimestamps[0];
  const previous = localStorage.highestUserReceived;
  if (latest && (!previous || latest > previous)) {
    localStorage.highestUserReceived = latest;
  }

  return (
    <div className="container-fluid">
      {reportSummaries.lastUpdatedField && (
        <Notification
          text="Incident Updated!"
          type="success"
          onDismiss={dismissNotification}
          show
        />
      )}
      {error.isActive && (
        <Notification
          text={`Error: ${error.message}`}
          type="danger"
          onDismiss={dismissNotification}
          show
        />
      )}
      {/* <div className="row">
        {newAudioReady &&
          reportSummaries.newItem.isNew &&
          reportSummaries.newItem.type === "public" && (
            <audio
              ref={newAudioRef}
              src={NewItemTone}
              autoPlay
              onEnded={() => dispatch(clearNewItem())}
            />
          )}
        {chimeAudioReady && latest && latest !== previous && (
          <audio ref={chimeAudioRef} src={WebChatChime} autoPlay />
        )}
        <audio ref={newAudioRef} src={NewItemTone} />
      <audio ref={chimeAudioRef} src={WebChatChime} />
    </div>
    */}
      {/* ✅ Give the H2 its own row so the closing </div> matches */}
       <div className="row">
      <h2 className="my-3 text-center" style={{ color: '#3f4b75' }}>
          {agency.details.name?.[languageCode]}
        </h2>
      </div> 

      <div className="row">
        {agency.details.display_pages && (
          <StateTabs
            toggleStateVisibility={toggleTabs}
            agency={agency}
            languageCode={languageCode}
            tabStatusDetails={tabStatus}
          />
        )}
      </div>
      <div className="row">
        <ReportSummaries
          dispatch={dispatch}
          summaries={reportSummaries.items}
          totalPages={reportSummaries.totalPages}
          newItem={reportSummaries.newItem}
          agency={agency}
          languageCode={languageCode}
         profile={{ details: profile }} 
          rowPerPage={rowPerPage}
          curPage={curPage}
          updateRowPerPage={updateRowPerPage}
          updateCurPage={updateCurPage}
        />
        <ModalRoot />
      </div>
    </div>
  );
}